import { useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Eye, ShieldCheck } from "lucide-react";

const CAT_LABEL = {
  handbook: { en: "Parent Handbook", fr: "Manuel des parents" },
  terms: { en: "Terms & Conditions", fr: "Termes et conditions" },
  other: { en: "Other", fr: "Autre" },
};

export default function Policies({ manage = false }) {
  const { L, lang } = useLang();
  const { data: policies, loading, reload } = useFetch(() => api.get("/policies"));
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("handbook");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const lg = lang === "fr" ? "fr" : "en";

  const upload = async () => {
    if (!title.trim() || !file) { toast.error(L("Title and PDF file are required", "Titre et fichier PDF requis")); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("title", title.trim()); fd.append("category", category); fd.append("file", file);
      await api.raw.post("/policies", fd);
      toast.success(L("Policy uploaded", "Politique téléversée")); setTitle(""); setFile(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setUploading(false); }
  };
  const del = async (p) => {
    if (!window.confirm(L(`Are you sure you want to delete ${p.title}?`, `Êtes-vous sûr de vouloir supprimer ${p.title} ?`))) return;
    try { await api.del(`/policies/${p.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("School Policies", "Politiques de l'école")}
        description={manage
          ? L("Upload the Parent Handbook and Terms as view-only PDFs for parents.", "Téléversez le manuel des parents et les termes en PDF (lecture seule) pour les parents.")
          : L("Read the school's policies. These documents are view-only.", "Consultez les politiques de l'école. Ces documents sont en lecture seule.")} />

      {manage && (
        <div className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="policy-upload-card">
          <p className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />{L("Add a policy document", "Ajouter un document")}</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label={L("Title", "Titre")}><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="policy-title-input" /></Field>
            <Field label={L("Category", "Catégorie")}>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="policy-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CAT_LABEL).map((k) => <SelectItem key={k} value={k}>{CAT_LABEL[k][lg]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={L("PDF file", "Fichier PDF")}><Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="policy-file-input" /></Field>
          </div>
          <Button className="mt-3" onClick={upload} disabled={uploading} data-testid="policy-upload-btn">{uploading ? L("Uploading…", "Téléversement…") : L("Upload", "Téléverser")}</Button>
        </div>
      )}

      {(policies || []).length === 0 ? <Empty title={L("No documents yet", "Aucun document")} /> : (
        <div className="space-y-3">
          {policies.map((p) => (
            <div key={p.id} className="bg-card border border-border rounded-lg" data-testid={`policy-${p.id}`}>
              <div className="p-4 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">{p.title}</p>
                    <Badge variant="secondary">{CAT_LABEL[p.category]?.[lg] || p.category}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setViewing(viewing === p.id ? null : p.id)} data-testid={`policy-view-${p.id}`}><Eye className="h-4 w-4 mr-1" />{viewing === p.id ? L("Hide", "Masquer") : L("Read", "Lire")}</Button>
                  {manage && <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`policy-del-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              </div>
              {viewing === p.id && (
                <div className="border-t border-border p-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2" data-testid={`policy-viewonly-note-${p.id}`}>
                    <ShieldCheck className="h-4 w-4" />
                    {L("View-only. Downloading and printing are disabled for parents.", "Lecture seule. Le téléchargement et l'impression sont désactivés pour les parents.")}
                  </div>
                  <div className="relative w-full" style={{ height: "70vh" }}>
                    <iframe title={p.title} src={`${API}/policies/${p.id}/view#toolbar=0&navpanes=0&scrollbar=0`} className="w-full h-full border border-border rounded" data-testid={`policy-frame-${p.id}`} />
                    {/* Overlay to discourage the built-in context menu / drag-save */}
                    <div className="absolute inset-0" style={{ pointerEvents: "none" }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
